import { getAllDocumentsWithCountFromDB, getDocumentByPriorityAirlineWithUnderscoreFromDB, getDocumentFromDB, getDocumentsByPriorityFromDB, getDocumentsFromDB, getDocumentsOrderedByWeightFromDB, getFlightCitiesForMultipleCodeFromDB, getIfRoutesFromDB, getTopAirlineDomesticRoutesFromDB, getTopAirlineRoutesFromDB } from './CommonUtils';
import { AIRLINE, AIRPORT, BLOCK, CITY, COUNTRY, DFROUTE, DFTemplate, FLIGHTS_NODE_TABLE, IFTemplate, INTL_AIRLINE, INTL_AIRLINE_ROUTES, INTL_AIRPORT, INTL_CITY, INTL_COUNTRY, ROUTE_DISTANCE, STATIC_PAGES_TEMPLATE } from './Constants';

export const getCityByFlightsUrlPhrase = async (urlPhrase) => {
  const city = await getDocumentFromDB(CITY, { flights_url_phrase: urlPhrase });
  return city;
};

export const getDFAirportForCityCode = async (cityCode) => {
  const airport = await getDocumentsFromDB(AIRPORT, { city_code: cityCode });
  return airport;
};

export const getIntlCountryByUrlPhrase = async (urlPhrase) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { url_phrase: urlPhrase });
  return country;
};

export const getIntlCountryByUrlPhraseAndShowCountryPage = async (urlPhrase, showCountryPage) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { url_phrase: urlPhrase });
  if (!country || country?.show_country_page !== showCountryPage) {
    return null;
  }
  return country;
};

export const getIntlCountryByCode = async (countryCode) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { code: countryCode });
  return country;
};

export const getIntlCountryByCodeAndShowCountryPage = async (countryCode, showCountryPage) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { code: countryCode });
  if (!country || country?.show_country_page !== showCountryPage) {
    return null;
  }
  return country;
};

export const getIntlCityByUrlPhrase = async (urlPhrase) => {
  const city = await getDocumentFromDB(INTL_CITY, { url_phrase: urlPhrase });
  return city;
};

export const getIntlCityByCountryIdWithCount = async (countryCode, order, count) => {
  const city = await getDocumentsByPriorityFromDB(INTL_CITY,
    { country_id: countryCode }, order, count);
  return city;
};

export const getAirportForCityCode = async (cityCode) => {
  const airport = await getDocumentsFromDB(INTL_AIRPORT, { city_code: cityCode });
  return airport;
};

export const getIntlCityByCityCodeAndCountryId = async (cityCode, countryCode) => {
  const city = await getDocumentFromDB(INTL_CITY,
    { city_code: cityCode, country_id: countryCode });
  return city;
};

export const getAirlineByUrlPhrase = async (dataMap, airlineUrlPhrase) => {
  const db = dataMap?.isIntl ? INTL_AIRLINE : AIRLINE;
  const airline = await getDocumentFromDB(db, { url_phrase: airlineUrlPhrase });
  return airline;
};

export const getAirlineByAlUrlPhrase = async (dataMap, airlineUrlPhrase) => {
  const db = dataMap?.isIntl ? INTL_AIRLINE : AIRLINE;
  const airline = await getDocumentFromDB(db, { al_url_phrase: airlineUrlPhrase });
  return airline;
};

export const getTemplateByType = async (dataMap, templateType) => {
  const type = dataMap?.isIntl ? IFTemplate : DFTemplate;
  const template = await getDocumentFromDB(type, { template_type: templateType });
  return template;
};

export const getBlocksForTemplate = async (templateType) => {
  const blocks = await getDocumentsOrderedByWeightFromDB(BLOCK, { content_type: templateType });
  return blocks;
};

export const getBlockByDelta = async (templateType, blockDelta) => {
  const block = await getDocumentsOrderedByWeightFromDB(BLOCK, { content_type: templateType,
    delta: blockDelta });
  return block;
};

export const getFlightNodeByURL = async (urlPhrase) => {
  const flightNode = await getDocumentsFromDB(FLIGHTS_NODE_TABLE, { url: urlPhrase });
  return flightNode;
};

export const getIntlCitiesForMultipleCode = async (cityCodes) => {
  const cityCodeArray = [...cityCodes];
  const cities = [];
  async function asyncForEach(array, callback) {
    // eslint-disable-next-line no-restricted-syntax
    for (const item of array) {
      // eslint-disable-next-line no-await-in-loop
      await callback(item);
    }
  }

  await asyncForEach(cityCodeArray, async (cityCode) => {
    // Simulate an asynchronous operation, e.g., fetching data
    const city = await getDocumentFromDB(INTL_CITY, { city_code: cityCode });
    cities.push(city);
  });
  return cities;
};

export const getRandomIntlCityByTopFlagWithCount = async (topFlag, count) => {
  const cities = await getDocumentsFromDB(INTL_CITY, { top_flag: topFlag });
  return cities.slice(0, count);
}

export const getRouteDistanceBySrcCityCodeAndDestCityCode = async (srcCityCode, destCityCode) => {
  const distance = await getDocumentFromDB(ROUTE_DISTANCE, { src_city_code: srcCityCode, dest_city_code: destCityCode });
  return distance;
};

export const getCountryDataByUrlPhrase = async (urlPhrase) => {
  const country = await getDocumentFromDB(COUNTRY, { url_phrase: urlPhrase });
  return country;
};

export const getDfAirlineByCode = async (airlineCode) => {
  const airline = await getDocumentFromDB(AIRLINE, { code: airlineCode });
  return airline;
};

export const getAllTopAirlineRoutes = async () => {
  const routes = await getDocumentsFromDB(DFROUTE, { is_airline_top_route: true });
  return routes;
};

export const getTemplateDataByUrlAndLob = async (url, lob) => {
  const template = await getDocumentFromDB(STATIC_PAGES_TEMPLATE, { url, lob_type: lob });
  return template;
};

export const getTemplateDataByUrl = async (url) => {
  const template = await getDocumentFromDB(STATIC_PAGES_TEMPLATE, { url });
  return template;
};

export const getTemplateDataByAlternateUrlAndLob = async (alternatUrl, lob) => {
  const template = await getDocumentFromDB(STATIC_PAGES_TEMPLATE, { alternat_url: alternatUrl, lob_type: lob });
  return template;
};

export const getBlocksForTemplateAndUrl = async (templateType, url) => {
  const blocks = await getDocumentsFromDB(BLOCK, { content_type: templateType });
  const result = blocks.filter((block) => {
    if (block.link_target && block.link_target !== '') {
      return block.link_target.includes(url);
    }
    return false;
  });
  return result;
};

export const getCityForCountryByTopFlag = async (countryId, topFlag) => {
  const city = await getDocumentFromDB(CITY, { country_id: countryId });
  let cityReturn = null;
  for (let i = 0; i < city.length; i += 1) {
    if (city.top_flag === topFlag) {
      cityReturn = city;
      break;
    }
  }
  return cityReturn;
};

export const getCitiesForCountryByTopFlag = async (countryId, topFlag) => {
  const cities = await getDocumentsFromDB(CITY, { country_id: countryId });
  const citiesReturn = [];
  cities.forEach((city) => {
    if (city.top_flag === topFlag) {
      citiesReturn.push(city);
    }
  });
  return citiesReturn;
};

export const getCitiesForCountryAndByAirportFlagWithCount = async (countryId, airportFlag, count) => {
  const cities = await getDocumentsFromDB(CITY, { country_id: countryId });
  const citiesReturn = [];
  cities.forEach((city) => {
    if (city.airport_flag === airportFlag) {
      citiesReturn.push(city);
    }
  });
  return citiesReturn.slice(0, count);
};

export const getCitiesForCountryAndByAirportFlag = async (countryId, airportFlag) => {
  const cities = await getDocumentsFromDB(CITY, { country_id: countryId });
  const citiesReturn = [];
  cities.forEach((city) => {
    if (city.airport_flag === airportFlag) {
      citiesReturn.push(city);
    }
  });
  return citiesReturn;
};

export const getCityByFlightCityCode = async (flightCityCode) => {
  const city = await getDocumentFromDB(CITY, { flight_city_code: flightCityCode });
  return city;
};

export const getByLob = async (lob) => {
  const textToLinkDictionary = await getDocumentsFromDB('text_to_link_mapping', { lob });
  return textToLinkDictionary;
};

export const getReviewsForSrcDest = async (src, dest) => {
  const reviews = await getDocumentsFromDB('flight_reviews', { src, dest });
  return reviews;
};

export const getIFCityByUrlPhrase = async (urlPhrase) => {
  const city = await getDocumentFromDB(INTL_CITY, { url_phrase: urlPhrase });
  return city;
};

export const getAirlinesByTopFlag = async (topFlag) => {
  const airlines = await getDocumentsFromDB(AIRLINE, { top_flag: topFlag });
  return airlines;
};

export const getAirlinesByTopFlagWithCount = async (topFlag, count) => {
  const airlines = await getDocumentsFromDB(AIRLINE, { top_flag: topFlag });
  return airlines.slice(0, count);
};

export const getIntlAirlinesByTopFlagWithCount = async (topFlag, count) => {
  const airlines = await getDocumentsFromDB(INTL_AIRLINE, { top_flag: topFlag });
  return airlines.slice(0, count);
};

export const getAirlineRoutesForPriorityWithCount = async (airlineCode, priority, count) => {
  const airlineFieldName = `priority_${airlineCode}`;
  const dfRoutes = await getDocumentsFromDB(DFROUTE, { [airlineFieldName]: priority });
  return dfRoutes.slice(0, count);
};

export const getCityByCityCode = async (cityCode) => {
  const city = await getDocumentFromDB(CITY, { city_code: cityCode });
  return city;
};

export const findByName = async (name) => {
  const responses = await getDocumentsFromDB('acg', { name });
  return responses;
};

export const getAllTopAirlineRoutesToCity = async (cityCode, flag) => {
  const routes = await getDocumentsFromDB(DFROUTE, { dest_city_code: cityCode, is_airline_top_route: flag });
  return routes;
};

export const getRouteFromCityWithCount = async (cityCode, count) => {
  const routes = await getDocumentsByPriorityFromDB(DFROUTE, { src_city_code: cityCode }, 'ASC', count);
  return routes;
};

export const getRouteToCityWithCount = async (cityCode, count) => {
  const routes = await getDocumentsByPriorityFromDB(DFROUTE, { dest_city_code: cityCode }, 'ASC', count);
  return routes;
};

export const getAllAirlines = async () => {
  const airlines = await getDocumentsFromDB(AIRLINE, {});
  return airlines;
};

export const getAirportByAirportUrlPhrase = async (airportUrlPhrase) => {
  const aiports = await getDocumentsFromDB(AIRPORT, { url_phrase: airportUrlPhrase });
  return aiports;
};

export const getCitiesByFlightCityCode = async (cityCode) => {
  const cities = await getDocumentsFromDB(CITY, { flight_city_code: cityCode });
  return cities;
};

export const getAirlineByCode = async (airlineCode) => {
  const airline = await getDocumentFromDB(AIRLINE, { code: airlineCode });
  return airline;
};

export const getIntlCityByLocusCityCode = async (locusCityCode) => {
  const city = await getDocumentsFromDB(INTL_CITY, {locus_city_code: locusCityCode});
  return city;
};

export const getIntlCityByTopFlagWithCount = async (topFlag, count) => {
  const cities = await getDocumentsFromDB(INTL_CITY, { top_flag: topFlag });
  return cities.slice(0, count);
};

export const getRoutesFromCityWithTopFlagAndCountAndType = async (srcCityUrlPhrase, topFlag, type, count) => {
  const intlRoutes = await getDocumentsFromDB('intl_route', { src_url_phrase: srcCityUrlPhrase, top_flag: topFlag, type });
  return intlRoutes.slice(0, count);
};

export const getIntlRouteByTopFlagWithCount = async (code, count) => {
  const routes = await getDocumentsFromDB(INTL_AIRLINE_ROUTES, { airline_code: code });
  return routes.slice(0, count);
};

export const getIntlAirlineByCode = async (code) => {
  const airline = await getDocumentFromDB(INTL_AIRLINE, { code });
  return airline;
};

export const getRoutesFromCityWithCountryIdAndTopFlagAndCountAndType = async (srcCityUrlPhrase, countryId, topFlag, type, count) => {
  const intlRoutes = await getDocumentsFromDB('intl_route', { src_url_phrase: srcCityUrlPhrase, dest_country_id: countryId, top_flag: topFlag, type });
  return intlRoutes.slice(0, count);
};

export const getIntlAirlineRouteFromSrcToDestWithCount = async (srcUrlPhrase, destUrlPhrase, count) => {
  const intlRoutes = await getDocumentsFromDB(INTL_AIRLINE_ROUTES, { src_url_phrase: srcUrlPhrase, dest_url_phrase: destUrlPhrase });
  return intlRoutes.slice(0, count);
};

export const getRoutesFromSpecificCityToTopCitiesOfCountryWithCount = async (srcCityUrlPhrase, destCountryCode, count) => {
  const intlRoutes = await getDocumentsFromDB('intl_route', { src_url_phrase: srcCityUrlPhrase, dest_country_id: destCountryCode });
  return intlRoutes.slice(0, count);
};

export const getRoutesBetweenCountriesWithCount = async (srcCountryID, destCountryId, type, count) => {
  const intlRoutes = await getDocumentsFromDB('intl_route', { src_country_id: srcCountryID, dest_country_id: destCountryId, type });
  return intlRoutes.slice(0, count);
};

export const getRoutesBetweenCountries = async (srcCountryID, destCountryId, count) => {
  const intlRoutes = await getDocumentsFromDB('intl_route', { src_country_id: srcCountryID, dest_country_id: destCountryId });
  return intlRoutes.slice(0, count);
};

export const getAirlineSpecificRoutesWithCount = async (count, priorityAirlineWithoutUnderscore) => {
  const airlineDomRoutes = await getDocumentByPriorityAirlineWithUnderscoreFromDB('dfroutes', count, priorityAirlineWithoutUnderscore);
  return airlineDomRoutes;
};

export const findByCountryId = async (countryId) => {
  const cities = await getDocumentsFromDB(INTL_CITY, { country_id: countryId });
  return cities;
};

export const getIntlNonIndiaRouteWithCityCodeAndCount = async (srcCityCode, excludeIndianCityCodes, count) => {
  let routes = await getDocumentsFromDB(INTL_AIRLINE_ROUTES, { src_city_code: srcCityCode });
  routes = routes.filter((r) => excludeIndianCityCodes.indexOf(r.dest_city_code) === -1 && excludeIndianCityCodes.indexOf(r.src_city_code) === -1);
  return routes.slice(0, count);
};

export const getTopIntlRouteFromSrcWithCount = async (count) => {
  const routes = await getAllDocumentsWithCountFromDB(INTL_AIRLINE_ROUTES, {}, count);
  return routes;
};

export const getTopRoutesFromDestination = async (cityCode, count) => {
  const routes = await getDocumentsFromDB(INTL_AIRLINE_ROUTES, { dest_city_code: cityCode });
  return routes.slice(0, count);
};

export const getRoutesToCityWithTopFlagAndCountAndType = async (destCityUrlPhrase, topFlag, type, count) => {
  const routes = await getDocumentsFromDB('intl_route', { dest_url_phrase: destCityUrlPhrase, top_flag: topFlag, type });
  return routes.slice(0, count);
};

export const getIFRoutes = async (isIntl, sectors) => {
  const collection = isIntl ? 'intl_route' : 'dfroutes';
  let routes = [];
  if (sectors) {
    const sector = JSON.stringify(sectors);
    routes = await getIfRoutesFromDB(collection, { sectors: sector });
  }
  return routes;
};


export const getRoutesFromCountryWithTopFlagTypeAndPriorityWithCount = async (countryID, topFlag, type, priority, count) => {
  const routes = await getDocumentsFromDB('intl_route', { src_country_id: countryID, top_flag: topFlag, type, priority });
  return routes.slice(0, count);
};

export const getRoutesFromCountryWithTopFlagTypeWithCount = async (countryID, topFlag, type, count) => {
  const routes = await getDocumentsFromDB('intl_route', { src_country_id: countryID, top_flag: topFlag, type });
  return routes.slice(0, count);
};

export const getIntlCityByTopFlagAndCountryIdWithCount = async (topFlag, countryId, count) => {
  const cities = await getDocumentsFromDB(INTL_CITY, { top_flag: topFlag, country_id: countryId });
  return cities.slice(0, count);
};

export const getTopAirlineRoutes = async (code, countryCode, count) => {
  const routes = await getTopAirlineRoutesFromDB(INTL_AIRLINE_ROUTES, { airline_code: code, src_country_id: countryCode, dest_country_id: countryCode, count });
  return routes;
};

export const getTopAirlineDomesticRoutes = async (code, countryCode, count) => {
  const routes = await getTopAirlineDomesticRoutesFromDB(INTL_AIRLINE_ROUTES, { airline_code: code, src_country_id: countryCode, dest_country_id: countryCode, count });
  return routes;
};

export const getFlightCitiesForMultipleCode = async (codes) => {
  const cities = await getFlightCitiesForMultipleCodeFromDB(CITY, { cityCodeSet: codes });
  return cities;
};

export const getAllIntlAirlines = async () => {
  const airlines = await getDocumentsFromDB(INTL_AIRLINE, {});
  return airlines;
};

export const getAirportByCodeCityCodeAndUrlPhrase = async (cityCode, airportCode, airportInUrl) => {
  const airport = await getDocumentFromDB(INTL_AIRPORT, { city_code: cityCode, code: airportCode, url_phrase: airportInUrl });
  return airport;
};

export const getRedirectByRedirectAndHash = async (url, hash) => {
  const redirectObj = await getDocumentFromDB('intl_redirect', { redirect: url, hash });
  return redirectObj;
};

export const getAirportByOldAlias = async (oldAlias) => {
  const airport = await getDocumentFromDB(INTL_AIRPORT, { old_alias: oldAlias });
  return airport;
};

export const getIntlCountryByUrlPhraseForCountryPage = async (urlPhrase) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { url_phrase: urlPhrase, show_country_page: true });
  return country;
};

export const getRedirectBySourceUrl = async (isIntl, sourceUrl) => {
  const table = isIntl ? 'intl_redirect' : 'flight_redirect';
  const redirect = await getDocumentFromDB(table, { source: sourceUrl });
  return redirect;
};

export const findByOldUrlAndEntityTypeAndLobAndCountryUrl = async (oldUrl, entityType, lob, countryUrl) => {
  const history = await getDocumentFromDB('flight_entity_history', { old_url: oldUrl, entity_type: entityType, lob, country_url: countryUrl });
  return history;
};

export const getHistoryByOldUrlAndEntityTypeAndLob = async (isIntl, oldUrl, entityType, lob) => {
  const history = await getDocumentFromDB(isIntl ? 'entity_history' : 'flight_entity_history', { old_url: oldUrl, entity_type: entityType, lob });
  return history;
};

export const getHistoryByOldUrlAndEntityTypeAndLobAndCityUrl = async (pathVar, entityType, lob, cityURL) => {
  const history = await getDocumentFromDB('entity_history', { old_url: pathVar, entity_type: entityType, lob, city_url: cityURL });
  return history;
};

export const getAirlineSpecificFieldsByUrlPhrase = async (airlineName, airlineFieldName) => {
  const airline = await getDocumentFromDB(AIRLINE, { url_phrase: airlineName });
  if (!airline) return null;
  const airlineFields = {};
  // eslint-disable-next-line camelcase
  const { name, code, url_phrase } = airline;
  airlineFields.name = name;
  airlineFields.code = code;
  // eslint-disable-next-line camelcase
  airlineFields.url_phrase = url_phrase;
  airlineFields[airlineFieldName] = airline[airlineFieldName];

  return airlineFields;
};

export const getRoutesBySrcAndPriority = async (cityCode, priority, count) => {
  const dfroutes = await getDocumentsByPriorityFromDB(DFROUTE, { src_city_code: cityCode, priority }, 'ASC', count);
  return dfroutes;
};

export const getRoutesByDestAndPriority = async (cityCode, priority, count) => {
  const dfroutes = await getDocumentsByPriorityFromDB(DFROUTE, { dest_city_code: cityCode, priority }, 'ASC', count);
  return dfroutes;
};

export const getRoutesByPriority = async (priority, count) => {
  const dfroutes = await getDocumentsByPriorityFromDB(DFROUTE, { priority }, 'ASC', count);
  return dfroutes;
};
